import React, { useState, useEffect } from 'react';
import { Grid, TextField, Button, Paper, Typography, Box, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { createRent, updateRent, getRentById, listCompanies } from '../../api';
import img from '../../assets/banner.jpg';

const RentForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = !!(location.state?.data || id);

  const frequencyOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'halfyearly', label: 'Half-Yearly' },
    { value: 'yearly', label: 'Yearly' }
  ];

  const [formData, setFormData] = useState({
    company: '',
    company_name: '',
    covered_location: '',
    covered_entity: '',
    purpose: '',
    rent_period: '',
    property_address: {
      street_address: '',
      city: '',
      state: '',
      country: ''
    },
    frequency: 'monthly',
    rent_amount: '',
    less_tds: '',
    net_payable: '',
    payment_due_date: '',
    paid_date: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    const loadFormData = async () => {
      try {
        // Fetch companies for dropdown
        const companiesData = await listCompanies();
        setCompanies(Array.isArray(companiesData) ? companiesData : []);

        // Load existing rent data if in edit mode
        const initial = location.state?.data;
        if (initial) {
          setFormData({
            company: initial.company || '',
            company_name: initial.company_name || '',
            covered_location: initial.location || '',
            covered_entity: '',
            purpose: initial.purpose || '',
            rent_period: initial.rent_period || '',
            property_address: {
              street_address: initial.property_address || '',
              city: '',
              state: '',
              country: ''
            },
            frequency: initial.frequency || 'monthly',
            rent_amount: initial.rent_amount || '',
            less_tds: initial.less_tds || '',
            net_payable: initial.net_payable || '',
            payment_due_date: initial.payment_due_date ? initial.payment_due_date.split('T')[0] : '',
            paid_date: initial.paid_date ? initial.paid_date.split('T')[0] : ''
          });
        } else if (id) {
          const fetched = await getRentById(id);
          setFormData({
            company: fetched.company || '',
            company_name: fetched.company_name || '',
            covered_location: fetched.location || '',
            covered_entity: '',
            purpose: fetched.purpose || '',
            rent_period: fetched.rent_period || '',
            property_address: {
              street_address: fetched.property_address || '',
              city: '',
              state: '',
              country: ''
            },
            frequency: fetched.frequency || 'monthly',
            rent_amount: fetched.rent_amount || '',
            less_tds: fetched.less_tds || '',
            net_payable: fetched.net_payable || '',
            payment_due_date: fetched.payment_due_date ? fetched.payment_due_date.split('T')[0] : '',
            paid_date: fetched.paid_date ? fetched.paid_date.split('T')[0] : ''
          });
        }
      } catch (err) {
        setError('Failed to load rent data or companies');
        console.error('Error loading rent data:', err);
      } finally {
        setLoadingData(false);
      }
    };
    loadFormData();
  }, [id, location.state]);

  const handleChange = (e, fieldType = null) => {
    const { name, value } = e.target;
    if (typeof fieldType === 'string' && fieldType.startsWith('property_address.')) {
      const addressField = fieldType.split('.')[1];
      setFormData({
        ...formData,
        property_address: {
          ...formData.property_address,
          [addressField]: value
        }
      });
    } else if (name === 'company') {
      // When company changes, auto-populate company_name and address fields
      const selectedCompany = companies.find(company => company.legal_name === value);
      setFormData({
        ...formData,
        company: value,
        company_name: selectedCompany ? selectedCompany.company_name || '' : '',
        property_address: {
          street_address: selectedCompany ? selectedCompany.street_address || '' : '',
          city: selectedCompany ? selectedCompany.city || '' : '',
          state: selectedCompany ? selectedCompany.state || '' : '',
          country: selectedCompany ? selectedCompany.country || '' : ''
        }
      });
    } else {
      const updatedFormData = { ...formData, [name]: value };

      // Auto-calculate Net Payable: Rent Amount - Less TDS
      const rentAmount = parseFloat(updatedFormData.rent_amount) || 0;
      const lessTds = parseFloat(updatedFormData.less_tds) || 0;
      updatedFormData.net_payable = (rentAmount - lessTds).toFixed(2);

      setFormData(updatedFormData);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEdit) {
        await updateRent(id || formData.id, formData);
      } else {
        await createRent(formData);
      }
      navigate('/view-all', {
        state: { message: isEdit ? 'Rent updated successfully!' : 'Rent created successfully!' }
      });
    } catch (err) {
      setError(err.message || 'Submission failed');
      console.error('Error submitting rent form:', err);
    } finally {
      setLoading(false);
    }
  };

  const isDateField = (type) => type === 'date';

  if (loadingData) return <Box textAlign="center" py={4}><CircularProgress /></Box>;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: `url(${img})`,
        backgroundSize: 'cover',
        backgroundAttachment: 'fixed',
        py: 5,
        px: 2
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 9,
          borderRadius: 3,
          backgroundColor: '#121212',
          color: 'text.primary',
          maxWidth: 1200,
          mx: 'auto',
          boxShadow: 3
        }}
      >
        <Typography variant="h6" align="center" gutterBottom>
          {isEdit ? 'Edit Rent' : 'Rent Expense Details'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={4} direction="column">
            {/* First Row: Company, Company Name */}
            <Grid container item spacing={4}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" style={{marginRight:'185px'}}>
                  <InputLabel id="company-label" sx={{ color: '#fcfbfbff' }}>
                    Company
                  </InputLabel>
                  <Select
                    labelId="company-label"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e0b256' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e0b256' },
                      color: '#fff',
                      backgroundColor: '#1e1e1e',
                    }}
                  >
                    <MenuItem value="">
                      <em>Select a company</em>
                    </MenuItem>
                    {companies.map((company) => (
                      <MenuItem key={company._id} value={company.legal_name}>
                        {company.legal_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Company Name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  disabled
                  sx={{
                    '& label.Mui-focused': { color: '#e0b256' },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#ccc' },
                      '&:hover fieldset': { borderColor: '#e0b256' },
                      '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                    }
                  }}
                  InputProps={{ sx: { py: 1 } }}
                />
              </Grid>
            </Grid>

            {/* Second Row: Covered Location, Covered Entity, Purpose, Rent Period */}
            <Grid container item spacing={4}>
              {[
                { label: 'Covered Location', name: 'covered_location' },
                { label: 'Covered Entity', name: 'covered_entity' },
                { label: 'Purpose', name: 'purpose' },
                { label: 'Rent Period', name: 'rent_period' }
              ].map((field) => (
                <Grid item xs={12} sm={3} key={field.name}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={field.label}
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleChange}
                    sx={{
                      '& label.Mui-focused': { color: '#e0b256' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#ccc' },
                        '&:hover fieldset': { borderColor: '#e0b256' },
                        '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                      }
                    }}
                    InputProps={{ sx: { py: 1 } }}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Third Row: Property Address (Street Address, City, State, Country) */}
            <Grid container item spacing={4}>
              {[
                { label: 'Street Address', name: 'property_address.street_address' },
                { label: 'City', name: 'property_address.city' },
                { label: 'State', name: 'property_address.state' },
                { label: 'Country', name: 'property_address.country' }
              ].map((field) => (
                <Grid item xs={12} sm={3} key={field.name}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={field.label}
                    name={field.name}
                    value={formData.property_address[field.name.split('.')[1]]}
                    onChange={(e) => handleChange(e, field.name)}
                    disabled
                    sx={{
                      '& label.Mui-focused': { color: '#e0b256' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#ccc' },
                        '&:hover fieldset': { borderColor: '#e0b256' },
                        '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                      }
                    }}
                    InputProps={{ sx: { py: 1 } }}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Fourth Row: Frequency */}
            <Grid container item spacing={4}>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#ccc' },
                    '&:hover fieldset': { borderColor: '#e0b256' },
                    '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                  }
                }}>
                  <InputLabel sx={{ 
                    '&.Mui-focused': { color: '#e0b256' },
                    fontSize: '1.2rem'
                  }}>Frequency</InputLabel>
                  <Select
                    value={formData.frequency}
                    name="frequency"
                    onChange={handleChange}
                    label="Frequency"
                    sx={{
                      fontSize: '1.2rem',
                      '& .MuiSelect-select': { py: 1.5 }
                    }}
                  >
                    {frequencyOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value} sx={{ fontSize: '1.2rem' }}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Fifth Row: Rent Amount, Less TDS, Net Payable */}
            <Grid container item spacing={4}>
              {[
                { label: 'Rent Amount', name: 'rent_amount', type: 'number' },
                { label: 'Less TDS', name: 'less_tds', type: 'number' },
                { label: 'Net Payable', name: 'net_payable', type: 'number', disabled: true }
              ].map((field) => (
                <Grid item xs={12} sm={4} key={field.name}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={field.label}
                    name={field.name}
                    type={field.type}
                    value={formData[field.name]}
                    onChange={handleChange}
                    disabled={field.disabled || false}
                    sx={{
                      '& label.Mui-focused': { color: '#e0b256' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#ccc' },
                        '&:hover fieldset': { borderColor: '#e0b256' },
                        '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                      }
                    }}
                    InputProps={{ sx: { py: 1 } }}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Sixth Row: Payment Due Date, Paid Date */}
            <Grid container item spacing={4}>
              {[
                { label: 'Payment Due Date', name: 'payment_due_date', type: 'date' },
                { label: 'Paid Date', name: 'paid_date', type: 'date' }
              ].map((field) => (
                <Grid item xs={12} sm={6} key={field.name}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={field.label}
                    name={field.name}
                    type={field.type}
                    value={formData[field.name]}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& label.Mui-focused': { color: '#e0b256' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#ccc' },
                        '&:hover fieldset': { borderColor: '#e0b256' },
                        '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                      }
                    }}
                    inputProps={{ sx: { py: 1 } }}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Submit and Cancel Buttons */}
            <Grid container item spacing={4}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  sx={{
                    py: 1.25,
                    mt: 1.5,
                    fontWeight: 'bold',
                    backgroundColor: '#e0b256',
                    color: '#000',
                    '&:hover': { backgroundColor: '#c99e3c' }
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : isEdit ? 'Update' : 'Submit'}
                </Button>
              </Grid>
              {isEdit && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{
                      py: 1.25,
                      mt: 1,
                      fontWeight: 'bold',
                      color: '#e0b256',
                      borderColor: '#e0b256',
                      '&:hover': { backgroundColor: '#222' }
                    }}
                    onClick={() => navigate('/view-all')}
                  >
                    Cancel
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default RentForm;

